import type { DealFormData } from './types'
import { getDefaultDealFormData } from './form-defaults'

const STORAGE_KEY = 'submissionFormDraft'
const STORAGE_VERSION = 1

type FileFieldKey =
  | 'logo'
  | 'banner'
  | 'pitchDeck'
  | 'whitelistCsv'
  | 'referralCustomCsv'
  | 'supportingDocuments'

type StoredFileMetadata = {
  name?: string
  type?: string
  size?: number
  lastModified?: number
  serverUrl?: string
  fallbackUrl?: string
}

type FileMetadataMap = Partial<Record<FileFieldKey, StoredFileMetadata>>

const FILE_FIELD_CONFIG: Record<
  FileFieldKey,
  {
    fallbackKey?: keyof DealFormData
    defaultName: string
    mime: string
  }
> = {
  logo: {
    fallbackKey: 'logoUrl',
    defaultName: 'logo.png',
    mime: 'image/png',
  },
  banner: {
    fallbackKey: 'bannerUrl',
    defaultName: 'banner.png',
    mime: 'image/png',
  },
  pitchDeck: {
    fallbackKey: 'pitchDeckUrl',
    defaultName: 'pitch-deck.pdf',
    mime: 'application/pdf',
  },
  whitelistCsv: {
    defaultName: 'whitelist.csv',
    mime: 'text/csv',
  },
  referralCustomCsv: {
    defaultName: 'custom-referrals.csv',
    mime: 'text/csv',
  },
  supportingDocuments: {
    fallbackKey: 'supportingDocumentsUrl',
    defaultName: 'supporting-document.pdf',
    mime: 'application/pdf',
  },
}

const FILE_FIELD_KEYS = Object.keys(FILE_FIELD_CONFIG) as FileFieldKey[]

type StoredDraft = {
  version: number
  data: Partial<DealFormData>
  completedSteps?: number[]
  updatedAt: number
  fileMetadata?: FileMetadataMap
}

const isBrowser = typeof window !== 'undefined' && !!window.localStorage

const safeParse = (raw: string | null): StoredDraft | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StoredDraft
    if (
      typeof parsed === 'object' &&
      typeof parsed.version === 'number' &&
      parsed.data &&
      typeof parsed.updatedAt === 'number'
    ) {
      return parsed
    }
  } catch (error) {
    console.error('Failed to parse submission draft', error)
  }
  return null
}

const extractFileMetadata = (
  file: File | null | undefined,
  fallbackUrl?: string,
): StoredFileMetadata | null => {
  if (!(file instanceof File)) return null
  const meta: StoredFileMetadata = {
    name: file.name || undefined,
    type: file.type || undefined,
    size: Number.isFinite(file.size) ? file.size : undefined,
    lastModified: Number.isFinite(file.lastModified)
      ? file.lastModified
      : undefined,
  }
  const serverUrl = (file as any)?.serverUrl
  if (typeof serverUrl === 'string' && serverUrl) meta.serverUrl = serverUrl
  if (fallbackUrl) meta.fallbackUrl = fallbackUrl
  return meta
}

const sanitizeDraftData = (
  rawData: Partial<DealFormData> = {},
): { data: Partial<DealFormData>; fileMetadata: FileMetadataMap } => {
  const sanitized: Partial<DealFormData> = { ...rawData }
  const metadata: FileMetadataMap = {}

  FILE_FIELD_KEYS.forEach((key) => {
    const config = FILE_FIELD_CONFIG[key]
    const fallbackUrl = config.fallbackKey
      ? (sanitized as any)[config.fallbackKey]
      : undefined
    const fileValue = (sanitized as any)[key]
    const meta = extractFileMetadata(fileValue, fallbackUrl)
    if (meta) metadata[key] = meta
    if (fileValue instanceof File) {
      ;(sanitized as any)[key] = null
    }
  })

  return { data: sanitized, fileMetadata: metadata }
}

const createFileFromMetadata = (
  key: FileFieldKey,
  meta: StoredFileMetadata | undefined,
  data: DealFormData,
): File | null => {
  if (!meta || typeof File === 'undefined') return null
  const config = FILE_FIELD_CONFIG[key]
  try {
    const file = new File([], meta.name || config.defaultName, {
      type: meta.type || config.mime,
      lastModified: meta.lastModified ?? Date.now(),
    }) as File & { serverUrl?: string; serverSize?: number }

    if (Number.isFinite(meta.size)) file.serverSize = meta.size

    const resolvedUrl =
      meta.serverUrl ||
      meta.fallbackUrl ||
      (config.fallbackKey ? ((data as any)[config.fallbackKey] as string) : '')

    if (resolvedUrl) file.serverUrl = resolvedUrl

    return file
  } catch {
    return null
  }
}

const rehydrateFileMetadata = (
  data: DealFormData,
  metadata?: FileMetadataMap,
) => {
  if (!metadata) return
  FILE_FIELD_KEYS.forEach((key) => {
    const file = createFileFromMetadata(key, metadata[key], data)
    if (file) (data as any)[key] = file
  })
}

export const saveFormDraft = (
  data: Partial<DealFormData> = {},
  completedSteps: number[] = [],
) => {
  if (!isBrowser) return
  try {
    const { data: sanitizedData, fileMetadata } = sanitizeDraftData(data)
    const payload: StoredDraft = {
      version: STORAGE_VERSION,
      data: sanitizedData,
      completedSteps,
      updatedAt: Date.now(),
    }
    if (Object.keys(fileMetadata).length > 0)
      payload.fileMetadata = fileMetadata
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (error) {
    console.warn('Unable to persist submission draft', error)
  }
}

export const loadFormDraft = (): {
  data: DealFormData
  completedSteps: number[]
} | null => {
  if (!isBrowser) return null
  const stored = safeParse(window.localStorage.getItem(STORAGE_KEY))
  if (!stored) return null
  if (stored.version !== STORAGE_VERSION) {
    clearFormDraft()
    return null
  }
  const data = { ...getDefaultDealFormData(), ...stored.data }
  rehydrateFileMetadata(data, stored.fileMetadata)
  return {
    data,
    completedSteps: Array.isArray(stored.completedSteps)
      ? stored.completedSteps
      : [],
  }
}

export const clearFormDraft = () => {
  if (!isBrowser) return
  window.localStorage.removeItem(STORAGE_KEY)
}
